import React, { useState } from 'react';
import { Box, ButtonBase, Tooltip } from '@material-ui/core';
import { VariantType } from 'notistack';
import { CustomIcon, CustomCheckbox, ConfirmDialog } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';
import { WorkflowCalculation } from '../../../interfaces/Workflow';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import { CalculationActions } from '../types/alternative-calculation.types';
import AlternativeCalculationCells from './AlternativeCalculationCells';

export interface AlternativeCalculationVariantRowProps {
  part: CaseCalculationPart;
  alternativePart: CaseCalculationAlternativePart;
  workflowCalculation: WorkflowCalculation | null;
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
}

const AlternativeCalculationVariantRow: React.FC<AlternativeCalculationVariantRowProps> = ({
  part,
  alternativePart,
  workflowCalculation,
  actions,
  onNotify
}) => {
  const c = useStyles();
  const t = useTranslate();

  const variantsEnabled = useVariantsEnabled();
  const selected = alternativePart.isVariantSelected === true;
  const recommended = alternativePart.isSelectedAlternativePart === true;
  const showChecked = selected || recommended;
  const [stockConfirm, setStockConfirm] = useState(false);
  const exceedsStock =
    alternativePart.availableQuantity != null && alternativePart.availableQuantity < part.pieceNr;

  // Any non-child variant is selectable; bundle children are locked (they follow their parent).
  const canSelectAlternativePart =
    workflowCalculation?.selectAlternativePart === true && !alternativePart.isChild;

  const doSelect = () => {
    if (variantsEnabled) {
      actions.toggleSelect(part.id, alternativePart);
      return;
    }
    // Phase 1 (single-select): clicking the already-selected variant clears it.
    if (selected) {
      actions.unselectPart(part.id);
      return;
    }
    actions.setRecommended(part.id, alternativePart);
  };

  const toggle = () => {
    if (!canSelectAlternativePart) {
      return;
    }
    if (!selected && exceedsStock) {
      setStockConfirm(true);
      return;
    }
    doSelect();
  };
  const confirmStock = () => {
    setStockConfirm(false);
    actions.logStockOverrideConfirm(part, alternativePart);
    doSelect();
  };
  const setReco = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Clicking the star on the already-recommended variant clears the recommendation.
    if (recommended) {
      actions.clearRecommendation(part.id);
      return;
    }
    actions.setRecommended(part.id, alternativePart);
  };

  return (
    <>
      <Box
        ref={(el: HTMLElement | null) => actions.registerRow(alternativePart.id, el)}
        role="checkbox"
        aria-checked={showChecked}
        tabIndex={canSelectAlternativePart ? 0 : -1}
        onClick={toggle}
        onKeyDown={(e) => {
          // Toggle only when the row itself is focused; inner buttons (star, copy) handle their own keys.
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            toggle();
          }
        }}
        className={cx(
          c.gridRow,
          c.resultRow,
          selected && c.resultRowSel,
          recommended && c.resultRowReco
        )}
        style={{ cursor: canSelectAlternativePart ? 'pointer' : 'not-allowed' }}
      >
        <Box>
          <CustomCheckbox
            checked={showChecked}
            disabled={!canSelectAlternativePart}
            readOnly
            tabIndex={-1}
            style={{ padding: 0 }}
          />
        </Box>
        <Box>
          {variantsEnabled && (
            <Tooltip
              title={
                recommended
                  ? t('ALT_CALC_RECO_ON', 'Recomandarea service-ului (informativ)')
                  : t('ALT_CALC_RECO_OFF', 'Marchează ca recomandată — doar informativ')
              }
              placement="left"
            >
              <ButtonBase
                className={cx(
                  c.iconBtn,
                  c.iconBtnSm,
                  c.iconBtnStar,
                  recommended && c.iconBtnStarOn
                )}
                disabled={alternativePart.isChild}
                onClick={setReco}
              >
                <CustomIcon name={recommended ? 'starFilled' : 'star'} />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
        <AlternativeCalculationCells
          part={part}
          alternativePart={alternativePart}
          workflowCalculation={workflowCalculation}
          onNotify={onNotify}
        />
      </Box>

      <ConfirmDialog
        open={stockConfirm}
        danger
        title={t('STOCK_EXCEEDED_CONFIRM_TITLE', 'Stoc insuficient')}
        message={t(
          'STOCK_EXCEEDED_CONFIRM_MESSAGE',
          'Cantitatea selectata depășește stocul disponibil. Doriți să continuați?'
        )}
        confirmLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
        cancelLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
        onConfirm={confirmStock}
        onClose={() => setStockConfirm(false)}
      />
    </>
  );
};

export default React.memo(AlternativeCalculationVariantRow);
